import {
  IoBriefcase,
  IoCalendarOutline,
  IoCallOutline,
  IoCash,
  IoCheckmarkCircleOutline,
  IoHeart,
  IoHome,
  IoLockClosedOutline,
  IoPeopleOutline,
  IoRibbonOutline,
  IoSchool,
  IoShieldCheckmarkOutline,
  IoSparkles,
  IoStar,
} from "react-icons/io5";
import type { ConsultationLandingConfig } from "./ConsultationLandingPage";

const sharedTestimonialStats = [
  { value: "20+", label: "Years Combined Experience" },
  { value: "50+", label: "Expert Astrologers" },
  { value: "70k+", label: "Happy Clients" },
  { value: "4.2*", label: "Average Rating" },
];

const genericTheme: ConsultationLandingConfig["theme"] = {
  pageBackgroundClass: "bg-background",
  heroSectionClass: "bg-gradient-to-br from-accent via-gray-900 to-accent",
  heroPatternClass: "opacity-[0.03]",
  heroTopOrbClass: "bg-primary/10",
  heroBottomOrbClass: "bg-gold/10",
  heroBottomCurveFill: "#F9F9F9",
  heroBadgeClass: "bg-white/10 backdrop-blur-sm border border-white/10",
  heroBadgeTextClass: "text-green-300",
  heroHighlightClass:
    "text-transparent bg-clip-text bg-gradient-to-r from-primary via-yellow-400 to-gold",
  heroSubtitleHighlightClass: "text-primary",
  heroPrimaryButtonClass:
    "w-full sm:w-auto bg-gradient-to-r from-primary to-orange-500 text-white font-bold text-lg px-8 py-4 rounded-full shadow-xl shadow-orange-500/30 hover:shadow-orange-500/50 hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-3",
  heroSecondaryButtonClass:
    "w-full sm:w-auto text-white/80 border border-white/20 hover:bg-white/10 font-semibold px-8 py-4 rounded-full transition-all flex items-center justify-center gap-2",
  expertsSectionClass: "bg-background",
  expertsBadgeClass: "bg-primary/10 text-primary",
  expertsBadgeTextClass: "text-primary",
  expertTitleClass: "text-primary",
  expertAvatarBorderClass: "border-primary/20",
  carouselButtonClass:
    "border border-orange-100 text-accent hover:border-primary/40 hover:text-primary",
  reasonsBadgeClass: "bg-orange-50 text-primary",
  reasonsBadgeTextClass: "text-primary",
  benefitsBadgeClass: "bg-green-50 text-green-700",
  benefitsBadgeTextClass: "text-green-700",
  processBadgeClass: "bg-primary/10 text-primary",
  processBadgeTextClass: "text-primary",
  stepCircleClass: "border-primary/20 group-hover:border-primary/40",
  stepNumberClass: "bg-gradient-to-br from-primary to-orange-600",
  stepBadgeClass:
    "bg-gradient-to-br from-primary to-orange-600 shadow-orange-500/30",
  stepConnectorClass:
    "bg-gradient-to-r from-primary/20 via-primary/40 to-primary/20",
  stepConnectorDashClass:
    "bg-[repeating-linear-gradient(90deg,transparent,transparent_8px,#FF993340_8px,#FF993340_16px)]",
  testimonialsBadgeClass: "bg-yellow-50 text-yellow-700",
  testimonialsBadgeTextClass: "text-yellow-700",
  testimonialAvatarClass: "bg-gradient-to-br from-primary to-orange-600",
  testimonialStatValueClass: "text-primary",
  finalSectionClass: "bg-background",
  trustCardIconShellClass: "bg-green-50",
  trustCardIconClass: "text-green-600",
  bookingCardClass:
    "bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden sticky top-[88px]",
  bookingCardHeaderClass: "bg-gradient-to-br from-accent to-gray-900",
  bookingCardBadgeClass: "bg-white/10 backdrop-blur-sm",
  bookingCardBadgeTextClass: "text-green-300",
  bookingCardIconShellClass: "bg-primary/10",
  bookingCardIconClass: "text-primary",
  bookingCardPrimaryButtonClass:
    "w-full bg-gradient-to-r from-primary to-orange-600 text-white font-bold text-lg py-5 rounded-2xl shadow-lg shadow-orange-500/20 hover:shadow-orange-500/40 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-3",
  stickyBarClass:
    "bg-white/95 backdrop-blur-lg shadow-[0_-2px_20px_rgba(0,0,0,0.08)] border-t border-gray-100",
  stickyButtonClass:
    "w-full md:w-auto bg-gradient-to-r from-primary to-orange-600 text-white px-8 py-3.5 rounded-full font-bold text-sm hover:shadow-lg hover:shadow-orange-500/20 transition-all flex items-center justify-center gap-2",
};

const careerTheme: ConsultationLandingConfig["theme"] = {
  pageBackgroundClass: "bg-slate-50",
  heroSectionClass: "bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950",
  heroPatternClass: "opacity-[0.04]",
  heroTopOrbClass: "bg-cyan-400/15",
  heroBottomOrbClass: "bg-amber-300/15",
  heroBottomCurveFill: "#F8FAFC",
  heroBadgeClass: "bg-white/10 backdrop-blur-sm border border-white/10",
  heroBadgeTextClass: "text-teal-200",
  heroHighlightClass:
    "text-transparent bg-clip-text bg-gradient-to-r from-cyan-200 via-teal-200 to-amber-200",
  heroSubtitleHighlightClass: "text-teal-200",
  heroPrimaryButtonClass:
    "w-full sm:w-auto bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-bold text-lg px-8 py-4 rounded-full shadow-xl shadow-cyan-950/30 hover:shadow-cyan-900/50 hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-3",
  heroSecondaryButtonClass:
    "w-full sm:w-auto text-white/80 border border-white/20 hover:bg-white/10 font-semibold px-8 py-4 rounded-full transition-all flex items-center justify-center gap-2",
  expertsSectionClass: "bg-slate-50",
  expertsBadgeClass: "bg-teal-50 text-teal-700",
  expertsBadgeTextClass: "text-teal-700",
  expertTitleClass: "text-teal-700",
  expertAvatarBorderClass: "border-teal-200",
  carouselButtonClass:
    "border border-slate-200 text-accent hover:border-teal-400 hover:text-teal-600",
  reasonsBadgeClass: "bg-slate-100 text-slate-700",
  reasonsBadgeTextClass: "text-slate-700",
  benefitsBadgeClass: "bg-teal-50 text-teal-700",
  benefitsBadgeTextClass: "text-teal-700",
  processBadgeClass: "bg-cyan-50 text-cyan-700",
  processBadgeTextClass: "text-cyan-700",
  stepCircleClass: "border-teal-200 group-hover:border-teal-400",
  stepNumberClass: "bg-gradient-to-br from-teal-500 to-cyan-500",
  stepBadgeClass:
    "bg-gradient-to-br from-slate-900 via-teal-700 to-cyan-600 shadow-teal-950/30",
  stepConnectorClass: "bg-gradient-to-r from-teal-200 via-cyan-300 to-amber-200",
  stepConnectorDashClass:
    "bg-[repeating-linear-gradient(90deg,transparent,transparent_8px,#0F766E33_8px,#0F766E33_16px)]",
  testimonialsBadgeClass: "bg-amber-50 text-amber-700",
  testimonialsBadgeTextClass: "text-amber-700",
  testimonialAvatarClass:
    "bg-gradient-to-br from-slate-900 via-teal-700 to-cyan-500",
  testimonialStatValueClass: "text-teal-700",
  finalSectionClass: "bg-slate-50",
  trustCardIconShellClass: "bg-teal-50",
  trustCardIconClass: "text-teal-700",
  bookingCardClass:
    "bg-white rounded-3xl shadow-xl border border-slate-200 overflow-hidden sticky top-[88px]",
  bookingCardHeaderClass: "bg-gradient-to-br from-slate-950 via-slate-900 to-teal-900",
  bookingCardBadgeClass: "bg-white/10 backdrop-blur-sm",
  bookingCardBadgeTextClass: "text-teal-200",
  bookingCardIconShellClass: "bg-teal-500/10",
  bookingCardIconClass: "text-teal-600",
  bookingCardPrimaryButtonClass:
    "w-full bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-bold text-lg py-5 rounded-2xl shadow-lg shadow-cyan-950/20 hover:shadow-cyan-900/30 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-3",
  stickyBarClass:
    "bg-white/95 backdrop-blur-lg shadow-[0_-2px_20px_rgba(15,23,42,0.1)] border-t border-slate-200",
  stickyButtonClass:
    "w-full md:w-auto bg-gradient-to-r from-teal-500 to-cyan-500 text-white px-8 py-3.5 rounded-full font-bold text-sm hover:shadow-lg hover:shadow-cyan-900/20 transition-all flex items-center justify-center gap-2",
};

const createIssueTheme = (
  overrides: Partial<ConsultationLandingConfig["theme"]>,
): ConsultationLandingConfig["theme"] => ({
  ...careerTheme,
  ...overrides,
});

const loveTheme = createIssueTheme({
  pageBackgroundClass: "bg-rose-50",
  heroSectionClass: "bg-gradient-to-br from-rose-950 via-fuchsia-950 to-pink-950",
  heroTopOrbClass: "bg-pink-300/15",
  heroBottomOrbClass: "bg-amber-200/15",
  heroBottomCurveFill: "#FFF1F2",
  heroBadgeTextClass: "text-rose-200",
  heroHighlightClass:
    "text-transparent bg-clip-text bg-gradient-to-r from-rose-100 via-pink-200 to-amber-100",
  heroSubtitleHighlightClass: "text-rose-200",
  heroPrimaryButtonClass:
    "w-full sm:w-auto bg-gradient-to-r from-rose-500 to-pink-500 text-white font-bold text-lg px-8 py-4 rounded-full shadow-xl shadow-pink-950/30 hover:shadow-pink-900/40 hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-3",
  expertsSectionClass: "bg-rose-50",
  expertsBadgeClass: "bg-rose-100 text-rose-700",
  expertsBadgeTextClass: "text-rose-700",
  expertTitleClass: "text-rose-700",
  expertAvatarBorderClass: "border-rose-200",
  carouselButtonClass:
    "border border-rose-200 text-accent hover:border-rose-400 hover:text-rose-600",
  reasonsBadgeClass: "bg-pink-100 text-pink-700",
  reasonsBadgeTextClass: "text-pink-700",
  benefitsBadgeClass: "bg-rose-100 text-rose-700",
  benefitsBadgeTextClass: "text-rose-700",
  processBadgeClass: "bg-pink-100 text-pink-700",
  processBadgeTextClass: "text-pink-700",
  stepCircleClass: "border-rose-200 group-hover:border-rose-400",
  stepNumberClass: "bg-gradient-to-br from-rose-500 to-pink-500",
  stepBadgeClass:
    "bg-gradient-to-br from-rose-600 to-pink-500 shadow-pink-950/30",
  stepConnectorClass: "bg-gradient-to-r from-rose-200 via-pink-300 to-amber-200",
  stepConnectorDashClass:
    "bg-[repeating-linear-gradient(90deg,transparent,transparent_8px,#E11D4833_8px,#E11D4833_16px)]",
  testimonialsBadgeClass: "bg-amber-50 text-amber-700",
  testimonialsBadgeTextClass: "text-amber-700",
  testimonialAvatarClass: "bg-gradient-to-br from-rose-600 to-pink-500",
  testimonialStatValueClass: "text-rose-700",
  finalSectionClass: "bg-rose-50",
  trustCardIconShellClass: "bg-rose-100",
  trustCardIconClass: "text-rose-700",
  bookingCardClass:
    "bg-white rounded-3xl shadow-xl border border-rose-200 overflow-hidden sticky top-[88px]",
  bookingCardHeaderClass: "bg-gradient-to-br from-rose-950 via-fuchsia-900 to-pink-900",
  bookingCardBadgeTextClass: "text-rose-200",
  bookingCardIconShellClass: "bg-rose-500/10",
  bookingCardIconClass: "text-rose-600",
  bookingCardPrimaryButtonClass:
    "w-full bg-gradient-to-r from-rose-500 to-pink-500 text-white font-bold text-lg py-5 rounded-2xl shadow-lg shadow-pink-950/20 hover:shadow-pink-900/30 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-3",
  stickyBarClass:
    "bg-white/95 backdrop-blur-lg shadow-[0_-2px_20px_rgba(80,7,36,0.1)] border-t border-rose-200",
  stickyButtonClass:
    "w-full md:w-auto bg-gradient-to-r from-rose-500 to-pink-500 text-white px-8 py-3.5 rounded-full font-bold text-sm hover:shadow-lg hover:shadow-pink-900/20 transition-all flex items-center justify-center gap-2",
});

const financeTheme = createIssueTheme({
  pageBackgroundClass: "bg-emerald-50",
  heroSectionClass: "bg-gradient-to-br from-emerald-950 via-teal-950 to-slate-950",
  heroTopOrbClass: "bg-emerald-300/15",
  heroBottomOrbClass: "bg-yellow-200/15",
  heroBottomCurveFill: "#ECFDF5",
  heroBadgeTextClass: "text-emerald-200",
  heroHighlightClass:
    "text-transparent bg-clip-text bg-gradient-to-r from-emerald-100 via-teal-200 to-yellow-100",
  heroSubtitleHighlightClass: "text-emerald-200",
  heroPrimaryButtonClass:
    "w-full sm:w-auto bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold text-lg px-8 py-4 rounded-full shadow-xl shadow-emerald-950/30 hover:shadow-emerald-900/40 hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-3",
  expertsSectionClass: "bg-emerald-50",
  expertsBadgeClass: "bg-emerald-100 text-emerald-700",
  expertsBadgeTextClass: "text-emerald-700",
  expertTitleClass: "text-emerald-700",
  expertAvatarBorderClass: "border-emerald-200",
  carouselButtonClass:
    "border border-emerald-200 text-accent hover:border-emerald-400 hover:text-emerald-600",
  reasonsBadgeClass: "bg-teal-100 text-teal-700",
  reasonsBadgeTextClass: "text-teal-700",
  benefitsBadgeClass: "bg-emerald-100 text-emerald-700",
  benefitsBadgeTextClass: "text-emerald-700",
  processBadgeClass: "bg-yellow-50 text-yellow-700",
  processBadgeTextClass: "text-yellow-700",
  stepCircleClass: "border-emerald-200 group-hover:border-emerald-400",
  stepNumberClass: "bg-gradient-to-br from-emerald-500 to-teal-500",
  stepBadgeClass:
    "bg-gradient-to-br from-emerald-600 to-teal-500 shadow-emerald-950/25",
  stepConnectorClass:
    "bg-gradient-to-r from-emerald-200 via-teal-300 to-yellow-200",
  stepConnectorDashClass:
    "bg-[repeating-linear-gradient(90deg,transparent,transparent_8px,#05966933_8px,#05966933_16px)]",
  testimonialsBadgeClass: "bg-yellow-50 text-yellow-700",
  testimonialsBadgeTextClass: "text-yellow-700",
  testimonialAvatarClass:
    "bg-gradient-to-br from-emerald-700 to-teal-500",
  testimonialStatValueClass: "text-emerald-700",
  finalSectionClass: "bg-emerald-50",
  trustCardIconShellClass: "bg-emerald-100",
  trustCardIconClass: "text-emerald-700",
  bookingCardClass:
    "bg-white rounded-3xl shadow-xl border border-emerald-200 overflow-hidden sticky top-[88px]",
  bookingCardHeaderClass: "bg-gradient-to-br from-emerald-950 via-teal-900 to-slate-950",
  bookingCardBadgeTextClass: "text-emerald-200",
  bookingCardIconShellClass: "bg-emerald-500/10",
  bookingCardIconClass: "text-emerald-600",
  bookingCardPrimaryButtonClass:
    "w-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold text-lg py-5 rounded-2xl shadow-lg shadow-emerald-950/20 hover:shadow-emerald-900/30 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-3",
  stickyBarClass:
    "bg-white/95 backdrop-blur-lg shadow-[0_-2px_20px_rgba(6,95,70,0.1)] border-t border-emerald-200",
  stickyButtonClass:
    "w-full md:w-auto bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-8 py-3.5 rounded-full font-bold text-sm hover:shadow-lg hover:shadow-emerald-900/20 transition-all flex items-center justify-center gap-2",
});

const childrenTheme = createIssueTheme({
  pageBackgroundClass: "bg-sky-50",
  heroSectionClass: "bg-gradient-to-br from-sky-950 via-blue-900 to-indigo-950",
  heroTopOrbClass: "bg-sky-300/15",
  heroBottomOrbClass: "bg-yellow-200/15",
  heroBottomCurveFill: "#F0F9FF",
  heroBadgeTextClass: "text-sky-200",
  heroHighlightClass:
    "text-transparent bg-clip-text bg-gradient-to-r from-sky-100 via-cyan-200 to-yellow-100",
  heroSubtitleHighlightClass: "text-sky-200",
  heroPrimaryButtonClass:
    "w-full sm:w-auto bg-gradient-to-r from-sky-500 to-indigo-500 text-white font-bold text-lg px-8 py-4 rounded-full shadow-xl shadow-sky-950/30 hover:shadow-sky-900/40 hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-3",
  expertsSectionClass: "bg-sky-50",
  expertsBadgeClass: "bg-sky-100 text-sky-700",
  expertsBadgeTextClass: "text-sky-700",
  expertTitleClass: "text-sky-700",
  expertAvatarBorderClass: "border-sky-200",
  carouselButtonClass:
    "border border-sky-200 text-accent hover:border-sky-400 hover:text-sky-600",
  reasonsBadgeClass: "bg-indigo-100 text-indigo-700",
  reasonsBadgeTextClass: "text-indigo-700",
  benefitsBadgeClass: "bg-sky-100 text-sky-700",
  benefitsBadgeTextClass: "text-sky-700",
  processBadgeClass: "bg-cyan-100 text-cyan-700",
  processBadgeTextClass: "text-cyan-700",
  stepCircleClass: "border-sky-200 group-hover:border-sky-400",
  stepNumberClass: "bg-gradient-to-br from-sky-500 to-indigo-500",
  stepBadgeClass:
    "bg-gradient-to-br from-sky-600 to-indigo-500 shadow-sky-950/25",
  stepConnectorClass:
    "bg-gradient-to-r from-sky-200 via-cyan-300 to-yellow-200",
  stepConnectorDashClass:
    "bg-[repeating-linear-gradient(90deg,transparent,transparent_8px,#0EA5E933_8px,#0EA5E933_16px)]",
  testimonialsBadgeClass: "bg-yellow-50 text-yellow-700",
  testimonialsBadgeTextClass: "text-yellow-700",
  testimonialAvatarClass:
    "bg-gradient-to-br from-sky-700 to-indigo-500",
  testimonialStatValueClass: "text-sky-700",
  finalSectionClass: "bg-sky-50",
  trustCardIconShellClass: "bg-sky-100",
  trustCardIconClass: "text-sky-700",
  bookingCardClass:
    "bg-white rounded-3xl shadow-xl border border-sky-200 overflow-hidden sticky top-[88px]",
  bookingCardHeaderClass: "bg-gradient-to-br from-sky-950 via-blue-900 to-indigo-900",
  bookingCardBadgeTextClass: "text-sky-200",
  bookingCardIconShellClass: "bg-sky-500/10",
  bookingCardIconClass: "text-sky-600",
  bookingCardPrimaryButtonClass:
    "w-full bg-gradient-to-r from-sky-500 to-indigo-500 text-white font-bold text-lg py-5 rounded-2xl shadow-lg shadow-sky-950/20 hover:shadow-sky-900/30 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-3",
  stickyBarClass:
    "bg-white/95 backdrop-blur-lg shadow-[0_-2px_20px_rgba(14,116,144,0.1)] border-t border-sky-200",
  stickyButtonClass:
    "w-full md:w-auto bg-gradient-to-r from-sky-500 to-indigo-500 text-white px-8 py-3.5 rounded-full font-bold text-sm hover:shadow-lg hover:shadow-sky-900/20 transition-all flex items-center justify-center gap-2",
});

const familyTheme = createIssueTheme({
  pageBackgroundClass: "bg-violet-50",
  heroSectionClass: "bg-gradient-to-br from-violet-950 via-indigo-950 to-slate-950",
  heroTopOrbClass: "bg-violet-300/15",
  heroBottomOrbClass: "bg-rose-200/15",
  heroBottomCurveFill: "#F5F3FF",
  heroBadgeTextClass: "text-violet-200",
  heroHighlightClass:
    "text-transparent bg-clip-text bg-gradient-to-r from-violet-100 via-indigo-200 to-rose-100",
  heroSubtitleHighlightClass: "text-violet-200",
  heroPrimaryButtonClass:
    "w-full sm:w-auto bg-gradient-to-r from-violet-500 to-indigo-500 text-white font-bold text-lg px-8 py-4 rounded-full shadow-xl shadow-violet-950/30 hover:shadow-violet-900/40 hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-3",
  expertsSectionClass: "bg-violet-50",
  expertsBadgeClass: "bg-violet-100 text-violet-700",
  expertsBadgeTextClass: "text-violet-700",
  expertTitleClass: "text-violet-700",
  expertAvatarBorderClass: "border-violet-200",
  carouselButtonClass:
    "border border-violet-200 text-accent hover:border-violet-400 hover:text-violet-600",
  reasonsBadgeClass: "bg-indigo-100 text-indigo-700",
  reasonsBadgeTextClass: "text-indigo-700",
  benefitsBadgeClass: "bg-violet-100 text-violet-700",
  benefitsBadgeTextClass: "text-violet-700",
  processBadgeClass: "bg-rose-100 text-rose-700",
  processBadgeTextClass: "text-rose-700",
  stepCircleClass: "border-violet-200 group-hover:border-violet-400",
  stepNumberClass: "bg-gradient-to-br from-violet-500 to-indigo-500",
  stepBadgeClass:
    "bg-gradient-to-br from-violet-600 to-indigo-500 shadow-violet-950/25",
  stepConnectorClass:
    "bg-gradient-to-r from-violet-200 via-indigo-300 to-rose-200",
  stepConnectorDashClass:
    "bg-[repeating-linear-gradient(90deg,transparent,transparent_8px,#8B5CF633_8px,#8B5CF633_16px)]",
  testimonialsBadgeClass: "bg-rose-50 text-rose-700",
  testimonialsBadgeTextClass: "text-rose-700",
  testimonialAvatarClass:
    "bg-gradient-to-br from-violet-700 to-indigo-500",
  testimonialStatValueClass: "text-violet-700",
  finalSectionClass: "bg-violet-50",
  trustCardIconShellClass: "bg-violet-100",
  trustCardIconClass: "text-violet-700",
  bookingCardClass:
    "bg-white rounded-3xl shadow-xl border border-violet-200 overflow-hidden sticky top-[88px]",
  bookingCardHeaderClass: "bg-gradient-to-br from-violet-950 via-indigo-900 to-slate-950",
  bookingCardBadgeTextClass: "text-violet-200",
  bookingCardIconShellClass: "bg-violet-500/10",
  bookingCardIconClass: "text-violet-600",
  bookingCardPrimaryButtonClass:
    "w-full bg-gradient-to-r from-violet-500 to-indigo-500 text-white font-bold text-lg py-5 rounded-2xl shadow-lg shadow-violet-950/20 hover:shadow-violet-900/30 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-3",
  stickyBarClass:
    "bg-white/95 backdrop-blur-lg shadow-[0_-2px_20px_rgba(76,29,149,0.08)] border-t border-violet-200",
  stickyButtonClass:
    "w-full md:w-auto bg-gradient-to-r from-violet-500 to-indigo-500 text-white px-8 py-3.5 rounded-full font-bold text-sm hover:shadow-lg hover:shadow-violet-900/20 transition-all flex items-center justify-center gap-2",
});

const lifeDirectionTheme = createIssueTheme({
  pageBackgroundClass: "bg-indigo-50",
  heroSectionClass: "bg-gradient-to-br from-indigo-950 via-blue-950 to-slate-950",
  heroTopOrbClass: "bg-indigo-300/15",
  heroBottomOrbClass: "bg-amber-200/15",
  heroBottomCurveFill: "#EEF2FF",
  heroBadgeTextClass: "text-indigo-200",
  heroHighlightClass:
    "text-transparent bg-clip-text bg-gradient-to-r from-indigo-100 via-blue-200 to-amber-100",
  heroSubtitleHighlightClass: "text-indigo-200",
  heroPrimaryButtonClass:
    "w-full sm:w-auto bg-gradient-to-r from-indigo-500 to-blue-500 text-white font-bold text-lg px-8 py-4 rounded-full shadow-xl shadow-indigo-950/30 hover:shadow-indigo-900/40 hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-3",
  expertsSectionClass: "bg-indigo-50",
  expertsBadgeClass: "bg-indigo-100 text-indigo-700",
  expertsBadgeTextClass: "text-indigo-700",
  expertTitleClass: "text-indigo-700",
  expertAvatarBorderClass: "border-indigo-200",
  carouselButtonClass:
    "border border-indigo-200 text-accent hover:border-indigo-400 hover:text-indigo-600",
  reasonsBadgeClass: "bg-blue-100 text-blue-700",
  reasonsBadgeTextClass: "text-blue-700",
  benefitsBadgeClass: "bg-indigo-100 text-indigo-700",
  benefitsBadgeTextClass: "text-indigo-700",
  processBadgeClass: "bg-amber-50 text-amber-700",
  processBadgeTextClass: "text-amber-700",
  stepCircleClass: "border-indigo-200 group-hover:border-indigo-400",
  stepNumberClass: "bg-gradient-to-br from-indigo-500 to-blue-500",
  stepBadgeClass:
    "bg-gradient-to-br from-indigo-600 to-blue-500 shadow-indigo-950/25",
  stepConnectorClass:
    "bg-gradient-to-r from-indigo-200 via-blue-300 to-amber-200",
  stepConnectorDashClass:
    "bg-[repeating-linear-gradient(90deg,transparent,transparent_8px,#4F46E533_8px,#4F46E533_16px)]",
  testimonialsBadgeClass: "bg-amber-50 text-amber-700",
  testimonialsBadgeTextClass: "text-amber-700",
  testimonialAvatarClass:
    "bg-gradient-to-br from-indigo-700 to-blue-500",
  testimonialStatValueClass: "text-indigo-700",
  finalSectionClass: "bg-indigo-50",
  trustCardIconShellClass: "bg-indigo-100",
  trustCardIconClass: "text-indigo-700",
  bookingCardClass:
    "bg-white rounded-3xl shadow-xl border border-indigo-200 overflow-hidden sticky top-[88px]",
  bookingCardHeaderClass: "bg-gradient-to-br from-indigo-950 via-blue-900 to-slate-950",
  bookingCardBadgeTextClass: "text-indigo-200",
  bookingCardIconShellClass: "bg-indigo-500/10",
  bookingCardIconClass: "text-indigo-600",
  bookingCardPrimaryButtonClass:
    "w-full bg-gradient-to-r from-indigo-500 to-blue-500 text-white font-bold text-lg py-5 rounded-2xl shadow-lg shadow-indigo-950/20 hover:shadow-indigo-900/30 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-3",
  stickyBarClass:
    "bg-white/95 backdrop-blur-lg shadow-[0_-2px_20px_rgba(30,64,175,0.08)] border-t border-indigo-200",
  stickyButtonClass:
    "w-full md:w-auto bg-gradient-to-r from-indigo-500 to-blue-500 text-white px-8 py-3.5 rounded-full font-bold text-sm hover:shadow-lg hover:shadow-indigo-900/20 transition-all flex items-center justify-center gap-2",
});

export const genericConsultationLandingConfig: ConsultationLandingConfig = {
  modalTheme: "generic",
  theme: genericTheme,
  hero: {
    badge: "Live Consultations Available Now",
    titlePrefix: "Confused? Stuck? Worried?",
    titleHighlight: "We've Got You.",
    descriptionPrefix: "Talk to India's trusted astrologers for",
    descriptionHighlight: "clear answers, fast.",
    supportingLine: "Har mod par, hum aapke saath hain.",
    primaryCtaText: "Book a Consultation",
    secondaryCtaText: "Meet Our Experts",
    stats: [
      {
        icon: <IoPeopleOutline />,
        value: "70,000+",
        label: "Happy Clients",
        iconShellClass: "bg-white/10",
        iconClass: "text-primary text-lg",
      },
      {
        icon: <IoStar />,
        value: "4.2*",
        label: "Avg. Rating",
        iconShellClass: "bg-white/10",
        iconClass: "text-yellow-400 text-lg",
      },
      {
        icon: <IoLockClosedOutline />,
        value: "100%",
        label: "Confidential",
        iconShellClass: "bg-white/10",
        iconClass: "text-green-400 text-lg",
      },
    ],
  },
  experts: {
    tag: "Our Expert Team",
    title: "India's Trusted Expert Consultation Panel",
    description:
      "Led by senior astrologers and certified experts who have handled thousands of consultations.",
    emptyState: "Our consultation experts will appear here shortly.",
  },
  reasons: {
    tag: "Common Consultation Reasons",
    title: "We understand what is worrying you",
    description:
      "From career and relationships to money and life clarity, this consultation helps turn confusion into action.",
    items: [
      {
        icon: <IoBriefcase />,
        label: "Career",
        problem: "Stuck in the same role and unsure what comes next?",
        solution:
          "Get clear timing, career direction, and practical remedies based on your chart.",
        color: "blue",
        href: "/consultations/career-job",
        ctaLabel: "Explore career page",
      },
      {
        icon: <IoHeart />,
        label: "Love & Marriage",
        problem: "Relationship confusion or marriage delays weighing on you?",
        solution:
          "Understand compatibility, timing, and relationship blocks with expert guidance.",
        color: "rose",
        href: "/consultations/love-marriage",
        ctaLabel: "Explore love page",
      },
      {
        icon: <IoCash />,
        label: "Finance",
        problem: "Constant money pressure or no breakthrough in business?",
        solution:
          "Spot money patterns, risk periods, and remedies that support stability.",
        color: "amber",
        href: "/consultations/finance",
        ctaLabel: "Explore finance page",
      },
      {
        icon: <IoSchool />,
        label: "Children",
        problem: "Worried about your child's direction, studies, or future?",
        solution:
          "Use chart guidance to understand strengths, timing, and the right direction.",
        color: "green",
        href: "/consultations/children",
        ctaLabel: "Explore children page",
      },
      {
        icon: <IoHome />,
        label: "Family",
        problem: "Tension at home or a sense that peace never lasts?",
        solution:
          "Spot repeating family patterns and get remedies for harmony and emotional balance.",
        color: "purple",
        href: "/consultations/family",
        ctaLabel: "Explore family page",
      },
      {
        icon: <IoSparkles />,
        label: "Life Direction",
        problem: "Feeling blocked, restless, or directionless in life?",
        solution:
          "Understand your current phase, what is changing, and where to focus next.",
        color: "orange",
        href: "/consultations/life-direction",
        ctaLabel: "Explore clarity page",
      },
    ],
  },
  benefits: {
    tag: "Consultation Benefits",
    title: "What You Get in This Consultation",
    description:
      "Each consultation is built to give clear direction, practical guidance, and peace of mind.",
    items: [
      {
        icon: <IoSparkles />,
        label: "Remedies",
        problem: "Powerful Personalized Remedies",
        solution:
          "Receive chart-based remedies to reduce blocks and support better outcomes.",
        color: "rose",
      },
      // {
      //   icon: <IoDiamond />,
      //   label: "Gemstones",
      //   problem: "Premium Rudraksha & Gemstone Recommendation",
      //   solution:
      //     "Get expert-picked Rudraksha and gemstones matched to your birth chart.",
      //   color: "amber",
      // },
      {
        icon: <IoCalendarOutline />,
        label: "Routine",
        problem: "Personalised Ritual Kit/Plan",
        solution:
          "Receive a personalised ritual kit and plan designed to align your energy and create positive change.",
        color: "green",
      },
    ],
  },
  process: {
    tag: "Simple Process",
    title: "3 Steps to Clarity",
    description: "Getting expert guidance is simple.",
    ctaText: "Book Your Consultation Now",
    items: [
      {
        number: "1",
        title: "Book Your Slot",
        description:
          "Fill the quick form, pick a time, and we will call you.",
        icon: <IoCalendarOutline />,
      },
      {
        number: "2",
        title: "Share Your Worries",
        description:
          "Speak openly with our expert astrologer. No judgment, just guidance.",
        icon: <IoCallOutline />,
      },
      {
        number: "3",
        title: "Get Real Solutions",
        description:
          "Get personalized remedies and practical next steps you can follow.",
        icon: <IoCheckmarkCircleOutline />,
      },
    ],
  },
  testimonials: {
    tag: "Client Stories",
    title: "Why 70,000+ People Trust Astroji",
    description: "Real concerns. Clear guidance.",
    stats: sharedTestimonialStats,
    items: [
      {
        name: "Rajesh Kumar",
        location: "Mumbai",
        problem: "Career Growth",
        rating: 5,
        text: "I was stuck in my job for years. After the consultation, I got promoted in three months. The remedies helped.",
      },
      {
        name: "Priya Sharma",
        location: "Delhi",
        problem: "Marriage",
        rating: 5,
        text: "I was worried about marriage delays. The guidance and remedies helped me find the right match in six months.",
      },
      {
        name: "Amit Patel",
        location: "Ahmedabad",
        problem: "Business",
        rating: 5,
        text: "My business was struggling. The suggested Vastu changes and remedies gave me better stability.",
      },
    ],
  },
  whyChoose: {
    title: "Why Choose Astroji?",
    description:
      "We combine Vedic wisdom with a practical, client-first approach so your guidance is clear and useful.",
    items: [
      {
        icon: <IoShieldCheckmarkOutline />,
        title: "100% Confidential",
        desc: "Your personal details and conversations stay private and secure.",
      },
      {
        icon: <IoRibbonOutline />,
        title: "Certified Vedic Experts",
        desc: "Our astrologers are certified and bring 12+ years of hands-on experience.",
      },
      {
        icon: <IoCheckmarkCircleOutline />,
        title: "Practical, Real-World Remedies",
        desc: "No complex rituals, just simple remedies that fit daily life.",
      },
      {
        icon: <IoPeopleOutline />,
        title: "Dedicated Follow-Up Support",
        desc: "We stay connected after your consultation to support your progress.",
      },
    ],
  },
  bookingCta: {
    liveBadge: "Limited Slots Today",
    title: "Book Your Session",
    description:
      "Join 70,000+ people who found clarity through expert astrological guidance.",
    buttonText: "Start Your Booking",
    privacyText: "Private & Secure",
    verifiedText: "Verified Experts",
  },
  stickyCta: {
    title: "Ready to find clarity?",
    subtitle: "Trusted by 70,000+ clients",
    buttonText: "Book Your Consultation",
  },
};

export const careerJobConsultationLandingConfig: ConsultationLandingConfig = {
  modalTheme: "career",
  modalDefaultConcernArea: "Career & Business",
  theme: careerTheme,
  hero: {
    badge: "Career & Job Consultation",
    titlePrefix: "Career stuck? Job growth delayed?",
    titleHighlight: "Let's bring clarity.",
    descriptionPrefix:
      "Talk to Astroji experts about promotions, job switches, office pressure, and your next move.",
    descriptionHighlight: "Clear guidance you can act on.",
    supportingLine: "From confusion to a confident next step.",
    primaryCtaText: "Book Your Consultation",
    secondaryCtaText: "Meet Our Experts",
    stats: [
      {
        icon: <IoPeopleOutline />,
        value: "70,000+",
        label: "Clients Guided",
        iconShellClass: "bg-white/10",
        iconClass: "text-teal-200 text-lg",
      },
      {
        icon: <IoStar />,
        value: "4.2*",
        label: "Trusted Rating",
        iconShellClass: "bg-white/10",
        iconClass: "text-amber-300 text-lg",
      },
      {
        icon: <IoLockClosedOutline />,
        value: "100%",
        label: "Private Sessions",
        iconShellClass: "bg-white/10",
        iconClass: "text-cyan-200 text-lg",
      },
    ],
  },
  experts: {
    tag: "Career Guidance Team",
    title: "Career & Job Guidance Backed by Astroji Experts",
    description:
      "Our experts help with promotion delays, job switches, office politics, and career confusion.",
    emptyState: "Our consultation experts will appear here shortly.",
  },
  reasons: {
    tag: "Career Pain Points",
    title: "Common career questions we solve",
    description:
      "If growth is slow, interviews stall, or office stress is rising, this page is for you.",
    items: [
      {
        icon: <IoBriefcase />,
        label: "Promotion Timing",
        problem: "Working hard but promotions keep slipping away?",
        solution:
          "Understand timing, blockers, and when your chart supports career growth.",
        color: "sky",
      },
      {
        icon: <IoCalendarOutline />,
        label: "Job Switch",
        problem: "Unsure whether to stay, switch, or wait?",
        solution:
          "See the best window for change, the right role type, and when to wait.",
        color: "teal",
      },
      {
        icon: <IoShieldCheckmarkOutline />,
        label: "Office Politics",
        problem: "Workplace politics chipping away at your confidence?",
        solution:
          "Spot hidden friction and get remedies for stability and self-protection at work.",
        color: "slate",
      },
      {
        icon: <IoCash />,
        label: "Salary Growth",
        problem: "Experience increasing but income staying flat?",
        solution:
          "Identify periods for salary growth, better negotiation, and stronger money decisions.",
        color: "amber",
      },
      {
        icon: <IoSparkles />,
        label: "Interview Confidence",
        problem: "Interviews go well but offers never land?",
        solution:
          "Understand timing, confidence blocks, and remedies before your next interviews.",
        color: "cyan",
      },
      {
        icon: <IoPeopleOutline />,
        label: "Career Direction",
        problem: "Doing the work but still feeling directionless?",
        solution:
          "Find a path that fits your strengths and long-term growth.",
        color: "indigo",
      },
    ],
  },
  benefits: {
    tag: "Career Consultation Benefits",
    title: "What this career consultation gives you",
    description:
      "This session helps you move from stress to clear timing, practical remedies, and a strong next step.",
    items: [
      {
        icon: <IoCalendarOutline />,
        label: "Timing",
        problem: "Career Timing Roadmap",
        solution:
          "See better periods for job change, promotion, and role growth.",
        color: "teal",
      },
      {
        icon: <IoBriefcase />,
        label: "Clarity",
        problem: "Role & Decision Clarity",
        solution:
          "Know whether to stay, negotiate, upskill, or pivot based on your current cycle.",
        color: "sky",
      },
      {
        icon: <IoShieldCheckmarkOutline />,
        label: "Remedies",
        problem: "Remedies for Work Obstacles",
        solution:
          "Get personalized remedies for delays, stress, rejections, and instability.",
        color: "amber",
      },
      {
        icon: <IoSparkles />,
        label: "Routine",
        problem: "Focused Confidence Plan",
        solution:
          "Get a simple routine to build consistency, confidence, and momentum.",
        color: "green",
      },
    ],
  },
  process: {
    tag: "Career Clarity Process",
    title: "3 Steps to move forward with confidence",
    description: "Simple and built around your current work situation.",
    ctaText: "Book Your Consultation Now",
    items: [
      {
        number: "1",
        title: "Book Your Consultation",
        description:
          "Reserve your slot and tell us where your career feels blocked.",
        icon: <IoCalendarOutline />,
      },
      {
        number: "2",
        title: "Share Your Context",
        description:
          "Share your role, setbacks, interview pattern, stress, or key decision.",
        icon: <IoCallOutline />,
      },
      {
        number: "3",
        title: "Get Your Action Plan",
        description:
          "Get timing guidance, remedies, and a practical next-step plan.",
        icon: <IoCheckmarkCircleOutline />,
      },
    ],
  },
  testimonials: {
    tag: "Career Stories",
    title: "Why people choose Astroji before their next work move",
    description:
      "Clear guidance for promotions, job switches, office pressure, and uncertainty.",
    stats: sharedTestimonialStats,
    items: [
      {
        name: "Neha Verma",
        location: "Bengaluru",
        problem: "Promotion Delay",
        rating: 5,
        text: "I was stuck across two appraisal cycles. The consultation gave me timing clarity and more confidence.",
      },
      {
        name: "Arjun Singh",
        location: "Gurugram",
        problem: "Job Switch",
        rating: 5,
        text: "I was unsure whether to stay or switch. The session gave me a clear window and made the decision easier.",
      },
      {
        name: "Pooja Nair",
        location: "Pune",
        problem: "Workplace Stress",
        rating: 5,
        text: "Office politics made me doubt myself. The guidance and remedies helped me feel in control again.",
      },
    ],
  },
  whyChoose: {
    title: "Why Astroji for Career Guidance?",
    description:
      "We combine timing insight with practical next steps you can use right away.",
    items: [
      {
        icon: <IoShieldCheckmarkOutline />,
        title: "Private Career Conversations",
        desc: "Speak openly about salary, stress, interviews, or office politics in a secure setting.",
      },
      {
        icon: <IoRibbonOutline />,
        title: "Experienced Guidance Team",
        desc: "Our experts understand career timing, pressure cycles, and recurring work obstacles.",
      },
      {
        icon: <IoCheckmarkCircleOutline />,
        title: "Practical Remedies + Direction",
        desc: "Get clear, practical suggestions that help you take action.",
      },
      {
        icon: <IoPeopleOutline />,
        title: "Support Beyond the Session",
        desc: "We stay connected after the call so your guidance stays supported.",
      },
    ],
  },
  bookingCta: {
    liveBadge: "Career Slots Filling Fast",
    title: "Book Your Career Consultation",
    description:
      "Talk to an Astroji expert about growth, job change, promotion timing, or workplace stress.",
    buttonText: "Start Your Booking",
    privacyText: "Private Guidance",
    verifiedText: "Verified Experts",
  },
  stickyCta: {
    title: "Need a clearer next career move?",
    subtitle: "Book your consultation today",
    buttonText: "Book Your Consultation",
  },
};

export const loveMarriageConsultationLandingConfig: ConsultationLandingConfig = {
  modalTheme: "love",
  modalDefaultConcernArea: "Love & Marriage",
  theme: loveTheme,
  hero: {
    badge: "Love & Marriage Consultation",
    titlePrefix: "Relationship confusion? Marriage timing unclear?",
    titleHighlight: "Let's make it easier to see.",
    descriptionPrefix:
      "Talk to Astroji experts about compatibility, delays, emotional distance, and your next step.",
    descriptionHighlight: "Calm guidance you can act on.",
    supportingLine: "When emotions feel heavy, clarity helps.",
    primaryCtaText: "Book Your Consultation",
    secondaryCtaText: "Meet Our Experts",
    stats: [
      {
        icon: <IoPeopleOutline />,
        value: "70,000+",
        label: "Clients Guided",
        iconShellClass: "bg-white/10",
        iconClass: "text-rose-200 text-lg",
      },
      {
        icon: <IoStar />,
        value: "4.2*",
        label: "Trusted Rating",
        iconShellClass: "bg-white/10",
        iconClass: "text-amber-300 text-lg",
      },
      {
        icon: <IoLockClosedOutline />,
        value: "100%",
        label: "Private Sessions",
        iconShellClass: "bg-white/10",
        iconClass: "text-pink-200 text-lg",
      },
    ],
  },
  experts: {
    tag: "Relationship Guidance Team",
    title: "Love & Marriage Guidance from Astroji Experts",
    description:
      "Our experts help with marriage delays, compatibility questions, instability, and emotional confusion.",
    emptyState: "Our consultation experts will appear here shortly.",
  },
  reasons: {
    tag: "Love Pain Points",
    title: "Common relationship questions we solve",
    description:
      "If your relationship feels uncertain, marriage is delayed, or the bond feels fragile, this page is for you.",
    items: [
      {
        icon: <IoHeart />,
        label: "Marriage Delay",
        problem: "Marriage conversations keep happening, but nothing moves forward?",
        solution:
          "Understand timing, blockers, and what your chart says about commitment.",
        color: "rose",
      },
      {
        icon: <IoPeopleOutline />,
        label: "Compatibility",
        problem: "Unsure whether the bond is truly aligned for the long term?",
        solution:
          "Get compatibility insight on emotional fit, friction points, and long-term potential.",
        color: "purple",
      },
      {
        icon: <IoShieldCheckmarkOutline />,
        label: "Family Approval",
        problem: "Outside pressure or family resistance making the relationship harder?",
        solution:
          "See what is causing delays and get remedies for steadier outcomes.",
        color: "amber",
      },
      {
        icon: <IoSparkles />,
        label: "Emotional Distance",
        problem: "Love is still there, but connection and warmth feel reduced?",
        solution:
          "Understand repeating patterns, communication strain, and what needs clarity.",
        color: "blue",
      },
      {
        icon: <IoCalendarOutline />,
        label: "Right Timing",
        problem: "Confused whether to wait, commit, reconcile, or let go?",
        solution:
          "Get timing support so your next decision feels less rushed.",
        color: "orange",
      },
      {
        icon: <IoCheckmarkCircleOutline />,
        label: "Next Step",
        problem: "You know something must change, but not what to do next?",
        solution:
          "Leave with a calmer view, practical remedies, and a clear next step.",
        color: "teal",
      },
    ],
  },
  benefits: {
    tag: "Love Consultation Benefits",
    title: "What this love and marriage consultation gives you",
    description:
      "This session helps reduce emotional confusion and gives you clarity and confidence.",
    items: [
      {
        icon: <IoHeart />,
        label: "Clarity",
        problem: "Relationship Clarity",
        solution:
          "Understand if this connection supports long-term growth and emotional security.",
        color: "rose",
      },
      {
        icon: <IoCalendarOutline />,
        label: "Timing",
        problem: "Marriage & Commitment Timing",
        solution:
          "See better periods for marriage talks, proposals, commitment, or waiting.",
        color: "amber",
      },
      {
        icon: <IoShieldCheckmarkOutline />,
        label: "Remedies",
        problem: "Remedies for Delays & Instability",
        solution:
          "Get personalized remedies for conflict, emotional heaviness, and outside pressure.",
        color: "purple",
      },
      {
        icon: <IoSparkles />,
        label: "Peace",
        problem: "A Calmer Emotional Plan",
        solution:
          "Leave with a simple plan that helps you feel calmer and less stuck.",
        color: "orange",
      },
    ],
  },
  process: {
    tag: "Relationship Clarity Process",
    title: "3 Steps to calmer relationship decisions",
    description: "Simple, private, and based on your current situation.",
    ctaText: "Book Your Consultation Now",
    items: [
      {
        number: "1",
        title: "Book Your Consultation",
        description:
          "Choose your slot and tell us if your concern is love, commitment, or marriage timing.",
        icon: <IoCalendarOutline />,
      },
      {
        number: "2",
        title: "Share Your Situation",
        description:
          "Talk openly about your relationship, delays, family pressure, or confusion over a live 1-1 video call.",
        icon: <IoCallOutline />,
      },
      {
        number: "3",
        title: "Get Your Guidance",
        description:
          "Get timing guidance, remedies, and a practical next-step plan.",
        icon: <IoCheckmarkCircleOutline />,
      },
    ],
  },
  testimonials: {
    tag: "Love Stories",
    title: "Why people choose Astroji when the heart feels uncertain",
    description:
      "Supportive guidance for compatibility, marriage delays, and difficult relationship choices.",
    stats: sharedTestimonialStats,
    items: [
      {
        name: "Ritika S.",
        location: "Delhi",
        problem: "Marriage Delay",
        rating: 5,
        text: "I was exhausted by repeated delays and mixed signals. The consultation gave me clear timing and peace of mind.",
      },
      {
        name: "Manish P.",
        location: "Jaipur",
        problem: "Compatibility",
        rating: 5,
        text: "I needed clarity before a long-term decision. The session showed where the relationship was strong and where honesty was needed.",
      },
      {
        name: "Sneha A.",
        location: "Mumbai",
        problem: "Relationship Stress",
        rating: 5,
        text: "The remedies and guidance helped me stay calm when I was overthinking everything.",
      },
    ],
  },
  whyChoose: {
    title: "Why Astroji for Love & Marriage Guidance?",
    description:
      "We keep relationship guidance sensitive, private, and timing-based so it feels supportive.",
    items: [
      {
        icon: <IoShieldCheckmarkOutline />,
        title: "Private Emotional Conversations",
        desc: "Speak honestly about love, marriage pressure, and compatibility in a secure setting.",
      },
      {
        icon: <IoRibbonOutline />,
        title: "Relationship-Focused Guidance",
        desc: "Our experts understand marriage timing, emotional patterns, and recurring relationship blocks.",
      },
      {
        icon: <IoCheckmarkCircleOutline />,
        title: "Remedies You Can Actually Follow",
        desc: "The guidance is practical, easy to follow, and useful beyond the session.",
      },
      {
        icon: <IoPeopleOutline />,
        title: "Support Beyond the Session",
        desc: "We stay connected after the call so your guidance stays supported.",
      },
    ],
  },
  bookingCta: {
    liveBadge: "Relationship Slots Filling Fast",
    title: "Book Your Love Consultation",
    description:
      "Talk to an Astroji expert about compatibility, marriage delays, or your next relationship decision.",
    buttonText: "Start Your Booking",
    privacyText: "Private Guidance",
    verifiedText: "Verified Experts",
  },
  stickyCta: {
    title: "Need clarity in love or marriage?",
    subtitle: "Book your private consultation",
    buttonText: "Book Your Consultation",
  },
};

export const financeConsultationLandingConfig: ConsultationLandingConfig = {
  modalTheme: "finance",
  modalDefaultConcernArea: "Finances & Wealth",
  theme: financeTheme,
  hero: {
    badge: "Finance & Wealth Consultation",
    titlePrefix: "Money pressure? Business stability unclear?",
    titleHighlight: "Let's bring structure to it.",
    descriptionPrefix:
      "Talk to Astroji experts about blocked income, business ups and downs, debt pressure, and money decisions.",
    descriptionHighlight: "Clear timing and calmer money choices.",
    supportingLine: "From financial stress to steady direction.",
    primaryCtaText: "Book Your Consultation",
    secondaryCtaText: "Meet Our Experts",
    stats: [
      {
        icon: <IoPeopleOutline />,
        value: "70,000+",
        label: "Clients Guided",
        iconShellClass: "bg-white/10",
        iconClass: "text-emerald-200 text-lg",
      },
      {
        icon: <IoStar />,
        value: "4.2*",
        label: "Trusted Rating",
        iconShellClass: "bg-white/10",
        iconClass: "text-yellow-200 text-lg",
      },
      {
        icon: <IoLockClosedOutline />,
        value: "100%",
        label: "Private Sessions",
        iconShellClass: "bg-white/10",
        iconClass: "text-teal-200 text-lg",
      },
    ],
  },
  experts: {
    tag: "Finance Guidance Team",
    title: "Finance & Wealth Guidance from Astroji Experts",
    description:
      "Our experts help with business volatility, salary pressure, money anxiety, and financial timing.",
    emptyState: "Our consultation experts will appear here shortly.",
  },
  reasons: {
    tag: "Money Pain Points",
    title: "Common money questions we solve",
    description:
      "If income feels blocked, business is unstable, or money pressure affects decisions, this page is for you.",
    items: [
      {
        icon: <IoCash />,
        label: "Income Block",
        problem: "Working hard, but money still feels stuck or unpredictable?",
        solution:
          "Understand money cycles, pressure periods, and where your chart supports better flow.",
        color: "green",
      },
      {
        icon: <IoBriefcase />,
        label: "Business Fluctuation",
        problem: "Business results swinging between hope and stress?",
        solution:
          "See timing patterns and remedies that support steadier business movement.",
        color: "teal",
      },
      {
        icon: <IoShieldCheckmarkOutline />,
        label: "Debt Pressure",
        problem: "Loans, obligations, or recurring money pressure feeling heavy?",
        solution:
          "Get guidance to handle pressure phases with more clarity and caution.",
        color: "slate",
      },
      {
        icon: <IoCalendarOutline />,
        label: "Investment Timing",
        problem: "Unsure whether to invest, expand, hold, or stay cautious?",
        solution:
          "Understand better timing windows so major money moves feel aligned.",
        color: "amber",
      },
      {
        icon: <IoSparkles />,
        label: "Breakthrough",
        problem: "No real financial breakthrough despite constant effort?",
        solution:
          "Decode hidden blockers and get remedies for progress and confidence.",
        color: "cyan",
      },
      {
        icon: <IoPeopleOutline />,
        label: "Money Anxiety",
        problem: "Financial stress affecting your confidence and daily peace?",
        solution:
          "Get guidance to make steady decisions instead of fear-based reactions.",
        color: "indigo",
      },
    ],
  },
  benefits: {
    tag: "Finance Consultation Benefits",
    title: "What this finance consultation gives you",
    description:
      "This session helps you move from stress to clear money timing, practical remedies, and stable choices.",
    items: [
      {
        icon: <IoCalendarOutline />,
        label: "Timing",
        problem: "Money Timing Roadmap",
        solution:
          "See stronger periods for investments, business moves, and financial recovery.",
        color: "green",
      },
      {
        icon: <IoCash />,
        label: "Clarity",
        problem: "Better Financial Decisions",
        solution:
          "Know whether this phase needs expansion, discipline, recovery, or a reset.",
        color: "teal",
      },
      {
        icon: <IoShieldCheckmarkOutline />,
        label: "Remedies",
        problem: "Remedies for Financial Obstacles",
        solution:
          "Get remedies for repeated loss, unstable cash flow, and blocked progress.",
        color: "amber",
      },
      {
        icon: <IoSparkles />,
        label: "Stability",
        problem: "A Steadier Money Plan",
        solution:
          "Leave with a simple plan for confidence, patience, and clear next steps.",
        color: "blue",
      },
    ],
  },
  process: {
    tag: "Money Clarity Process",
    title: "3 Steps to clearer financial direction",
    description: "Simple and built around your current money situation.",
    ctaText: "Book Your Consultation Now",
    items: [
      {
        number: "1",
        title: "Book Your Consultation",
        description:
          "Reserve your slot and tell us if your concern is income, business, debt, or uncertainty.",
        icon: <IoCalendarOutline />,
      },
      {
        number: "2",
        title: "Share Your Context",
        description:
          "Share your financial stress, business pattern, blocked growth, or key decision.",
        icon: <IoCallOutline />,
      },
      {
        number: "3",
        title: "Get Your Guidance",
        description:
          "Get timing guidance, remedies, and a practical financial direction.",
        icon: <IoCheckmarkCircleOutline />,
      },
    ],
  },
  testimonials: {
    tag: "Finance Stories",
    title: "Why people choose Astroji before major money decisions",
    description:
      "Clear guidance for business fluctuations, money pressure, and blocked income.",
    stats: sharedTestimonialStats,
    items: [
      {
        name: "Rahul G.",
        location: "Surat",
        problem: "Business Stress",
        rating: 5,
        text: "My business kept swinging between highs and drops. The consultation helped me make better timing decisions.",
      },
      {
        name: "Vidhi M.",
        location: "Ahmedabad",
        problem: "Money Block",
        rating: 5,
        text: "I was earning but could not save. The session gave me clarity on the pattern and what to stop forcing.",
      },
      {
        name: "Sandeep K.",
        location: "Noida",
        problem: "Debt Pressure",
        rating: 5,
        text: "The guidance helped me handle a difficult phase with more calm and a steadier mindset.",
      },
    ],
  },
  whyChoose: {
    title: "Why Astroji for Finance Guidance?",
    description:
      "We combine money timing, practical caution, and chart-based remedies you can use in real life.",
    items: [
      {
        icon: <IoShieldCheckmarkOutline />,
        title: "Private Financial Conversations",
        desc: "Speak openly about debt, business pressure, blocked income, or money stress in a secure setting.",
      },
      {
        icon: <IoRibbonOutline />,
        title: "Practical Wealth Guidance",
        desc: "Our experts understand financial timing, caution periods, and recovery cycles.",
      },
      {
        icon: <IoCheckmarkCircleOutline />,
        title: "Remedies + Decision Support",
        desc: "The session helps you make clear decisions, not just feel temporary relief.",
      },
      {
        icon: <IoPeopleOutline />,
        title: "Support Beyond the Session",
        desc: "We stay connected after the call so your guidance stays anchored.",
      },
    ],
  },
  bookingCta: {
    liveBadge: "Finance Slots Filling Fast",
    title: "Book Your Finance Consultation",
    description:
      "Talk to an Astroji expert about business stress, blocked income, debt pressure, or your next money move.",
    buttonText: "Start Your Booking",
    privacyText: "Private Guidance",
    verifiedText: "Verified Experts",
  },
  stickyCta: {
    title: "Need more financial clarity?",
    subtitle: "Book your consultation today",
    buttonText: "Book Your Consultation",
  },
};

export const childrenConsultationLandingConfig: ConsultationLandingConfig = {
  modalTheme: "children",
  modalDefaultConcernArea: "Education & Learning",
  theme: childrenTheme,
  hero: {
    badge: "Children Guidance Consultation",
    titlePrefix: "Worried about your child's studies or future?",
    titleHighlight: "Let's understand the phase better.",
    descriptionPrefix:
      "Talk to Astroji experts about education timing, confidence, behavior, focus, and your child's direction.",
    descriptionHighlight: "Thoughtful, parent-friendly support.",
    supportingLine: "Helping parents move from worry to steady guidance.",
    primaryCtaText: "Book Your Consultation",
    secondaryCtaText: "Meet Our Experts",
    stats: [
      {
        icon: <IoPeopleOutline />,
        value: "70,000+",
        label: "Families Guided",
        iconShellClass: "bg-white/10",
        iconClass: "text-sky-200 text-lg",
      },
      {
        icon: <IoStar />,
        value: "4.2*",
        label: "Trusted Rating",
        iconShellClass: "bg-white/10",
        iconClass: "text-yellow-200 text-lg",
      },
      {
        icon: <IoLockClosedOutline />,
        value: "100%",
        label: "Private Sessions",
        iconShellClass: "bg-white/10",
        iconClass: "text-cyan-200 text-lg",
      },
    ],
  },
  experts: {
    tag: "Children Guidance Team",
    title: "Children & Future Guidance from Astroji Experts",
    description:
      "Our experts help parents understand timing, behavior shifts, confidence dips, and future direction.",
    emptyState: "Our consultation experts will appear here shortly.",
  },
  reasons: {
    tag: "Parent Concerns",
    title: "Common child-related questions we solve",
    description:
      "If your child feels distracted, directionless, withdrawn, or stressed in studies, this page is for you.",
    items: [
      {
        icon: <IoSchool />,
        label: "Studies",
        problem: "Studies feel inconsistent despite effort and support?",
        solution:
          "Understand timing, focus cycles, and the support your child needs right now.",
        color: "sky",
      },
      {
        icon: <IoSparkles />,
        label: "Confidence",
        problem: "Confidence seems lower than before or easily shaken?",
        solution:
          "See emotional patterns affecting motivation and get remedies for confidence.",
        color: "indigo",
      },
      {
        icon: <IoPeopleOutline />,
        label: "Behaviour",
        problem: "Behaviour changes leaving you unsure how to respond?",
        solution:
          "Get insight into pressure patterns and how to respond with patience and clarity.",
        color: "cyan",
      },
      {
        icon: <IoCalendarOutline />,
        label: "Education Timing",
        problem: "Unsure about the right academic direction or timing?",
        solution:
          "Understand better windows for decisions around studies, exams, and transitions.",
        color: "amber",
      },
      {
        icon: <IoBriefcase />,
        label: "Future Direction",
        problem: "Worried your child still has no clear long-term direction?",
        solution:
          "Get chart-based insight into strengths, interests, and a clearer path forward.",
        color: "blue",
      },
      {
        icon: <IoHeart />,
        label: "Parental Stress",
        problem: "Your worry is growing because nothing feels settled yet?",
        solution:
          "Get support to guide your child with calm instead of constant fear.",
        color: "rose",
      },
    ],
  },
  benefits: {
    tag: "Children Consultation Benefits",
    title: "What this children guidance consultation gives you",
    description:
      "This session helps parents see the phase clearly, respond with confidence, and support their child better.",
    items: [
      {
        icon: <IoSchool />,
        label: "Direction",
        problem: "Education & Strength Clarity",
        solution:
          "Understand your child's strengths, learning style, and the path that fits best right now.",
        color: "sky",
      },
      {
        icon: <IoCalendarOutline />,
        label: "Timing",
        problem: "Better Timing for Decisions",
        solution:
          "See supportive periods for study focus, transitions, exams, and key decisions.",
        color: "amber",
      },
      {
        icon: <IoShieldCheckmarkOutline />,
        label: "Remedies",
        problem: "Remedies for Distraction & Pressure",
        solution:
          "Get remedies for confidence, stress, emotional heaviness, and repeated struggles.",
        color: "indigo",
      },
      {
        icon: <IoSparkles />,
        label: "Support",
        problem: "A Calmer Support Plan",
        solution:
          "Leave with a parent-friendly plan to support your child with steadiness.",
        color: "cyan",
      },
    ],
  },
  process: {
    tag: "Parent Guidance Process",
    title: "3 Steps to more confident guidance for your child",
    description: "Simple and built around your child's current situation.",
    ctaText: "Book Your Consultation Now",
    items: [
      {
        number: "1",
        title: "Book Your Consultation",
        description:
          "Reserve your slot and tell us if the concern is studies, focus, confidence, or future direction.",
        icon: <IoCalendarOutline />,
      },
      {
        number: "2",
        title: "Share Your Concerns",
        description:
          "Share the patterns you are seeing at home, in studies, or emotionally.",
        icon: <IoCallOutline />,
      },
      {
        number: "3",
        title: "Get Your Guidance",
        description:
          "Get timing guidance, remedies, and a calm support plan you can use as a parent.",
        icon: <IoCheckmarkCircleOutline />,
      },
    ],
  },
  testimonials: {
    tag: "Parent Stories",
    title: "Why families choose Astroji when worry starts taking over",
    description:
      "Supportive guidance for studies, confidence, behavior shifts, and future direction.",
    stats: sharedTestimonialStats,
    items: [
      {
        name: "Kavita R.",
        location: "Pune",
        problem: "Study Stress",
        rating: 5,
        text: "I was deeply worried about my son's focus and confidence. The session helped me respond with patience instead of fear.",
      },
      {
        name: "Harshita T.",
        location: "Indore",
        problem: "Future Direction",
        rating: 5,
        text: "I wanted clarity on my daughter's strengths and academic direction. The guidance was practical and reassuring.",
      },
      {
        name: "Nitin B.",
        location: "Lucknow",
        problem: "Behaviour Change",
        rating: 5,
        text: "The consultation gave us a calm way to handle recurring behavior changes. We felt less helpless and more intentional.",
      },
    ],
  },
  whyChoose: {
    title: "Why Astroji for Children Guidance?",
    description:
      "We help parents understand timing, strengths, and pressure phases so guidance feels supportive for the whole family.",
    items: [
      {
        icon: <IoShieldCheckmarkOutline />,
        title: "Private Family Conversations",
        desc: "Speak openly about your child's stress, studies, confidence, or behavior in a secure setting.",
      },
      {
        icon: <IoRibbonOutline />,
        title: "Parent-Friendly Guidance",
        desc: "Our experts turn chart insight into practical guidance for real family decisions.",
      },
      {
        icon: <IoCheckmarkCircleOutline />,
        title: "Practical Remedies + Direction",
        desc: "Get realistic support, clear next steps, and remedies you can follow.",
      },
      {
        icon: <IoPeopleOutline />,
        title: "Support Beyond the Session",
        desc: "We stay available after the call so your guidance stays supported.",
      },
    ],
  },
  bookingCta: {
    liveBadge: "Parent Guidance Slots Filling Fast",
    title: "Book Your Children Consultation",
    description:
      "Talk to an Astroji expert about your child's studies, confidence, future direction, or emotional well-being.",
    buttonText: "Start Your Booking",
    privacyText: "Private Guidance",
    verifiedText: "Verified Experts",
  },
  stickyCta: {
    title: "Need calmer guidance for your child?",
    subtitle: "Book your consultation today",
    buttonText: "Book Your Consultation",
  },
};

export const familyConsultationLandingConfig: ConsultationLandingConfig = {
  modalTheme: "family",
  modalDefaultConcernArea: "Family & Relationships",
  theme: familyTheme,
  hero: {
    badge: "Family Consultation",
    titlePrefix: "Tension at home? Family peace not lasting?",
    titleHighlight: "Let's understand the pattern.",
    descriptionPrefix:
      "Talk to Astroji experts about recurring conflict, emotional distance, and home stress.",
    descriptionHighlight: "Guidance that supports peace.",
    supportingLine: "When home feels heavy, clarity helps.",
    primaryCtaText: "Book Your Consultation",
    secondaryCtaText: "Meet Our Experts",
    stats: [
      {
        icon: <IoPeopleOutline />,
        value: "70,000+",
        label: "Families Guided",
        iconShellClass: "bg-white/10",
        iconClass: "text-violet-200 text-lg",
      },
      {
        icon: <IoStar />,
        value: "4.2*",
        label: "Trusted Rating",
        iconShellClass: "bg-white/10",
        iconClass: "text-rose-200 text-lg",
      },
      {
        icon: <IoLockClosedOutline />,
        value: "100%",
        label: "Private Sessions",
        iconShellClass: "bg-white/10",
        iconClass: "text-indigo-200 text-lg",
      },
    ],
  },
  experts: {
    tag: "Family Guidance Team",
    title: "Family Harmony Guidance from Astroji Experts",
    description:
      "Our experts help with home tension, repeated misunderstandings, family pressure, and emotional distance.",
    emptyState: "Our consultation experts will appear here shortly.",
  },
  reasons: {
    tag: "Family Pain Points",
    title: "Common family questions we solve",
    description:
      "If home feels heavy, misunderstandings repeat, or peace does not last, this page is for you.",
    items: [
      {
        icon: <IoHome />,
        label: "Home Tension",
        problem: "Peace at home feels temporary and easily disturbed?",
        solution:
          "Understand repeating family patterns and get remedies for emotional balance at home.",
        color: "purple",
      },
      {
        icon: <IoPeopleOutline />,
        label: "Misunderstandings",
        problem: "Small issues keep turning into larger emotional conflict?",
        solution:
          "See where friction repeats and how to respond with more steadiness.",
        color: "indigo",
      },
      {
        icon: <IoHeart />,
        label: "Emotional Distance",
        problem: "You live together, but warmth and connection feel reduced?",
        solution:
          "Get clarity on the emotional cycle and where healing can begin.",
        color: "rose",
      },
      {
        icon: <IoShieldCheckmarkOutline />,
        label: "Family Pressure",
        problem: "Family expectations or interference making life more stressful?",
        solution:
          "Understand pressure patterns and get remedies for calmer decisions and boundaries.",
        color: "amber",
      },
      {
        icon: <IoCalendarOutline />,
        label: "Property & Home Decisions",
        problem: "Unsure about the right timing for an important family or home decision?",
        solution:
          "Get timing insight for clearer family and home decisions.",
        color: "blue",
      },
      {
        icon: <IoSparkles />,
        label: "Peace of Mind",
        problem: "Family stress is draining your energy every day?",
        solution:
          "Leave with practical direction to restore calm and emotional balance.",
        color: "green",
      },
    ],
  },
  benefits: {
    tag: "Family Consultation Benefits",
    title: "What this family consultation gives you",
    description:
      "This session helps you understand root patterns, calm emotions, and restore family harmony.",
    items: [
      {
        icon: <IoHome />,
        label: "Harmony",
        problem: "Clarity on Home Energy",
        solution:
          "Understand what is disturbing harmony and which patterns need attention now.",
        color: "purple",
      },
      {
        icon: <IoCalendarOutline />,
        label: "Timing",
        problem: "Better Family Decision Timing",
        solution:
          "See supportive periods for important home and family decisions.",
        color: "amber",
      },
      {
        icon: <IoShieldCheckmarkOutline />,
        label: "Remedies",
        problem: "Remedies for Recurring Conflict",
        solution:
          "Get practical remedies that support peace and reduce repeated friction.",
        color: "rose",
      },
      {
        icon: <IoSparkles />,
        label: "Calm",
        problem: "A Simpler Peace Plan",
        solution:
          "Leave with a grounded plan to create a calmer emotional atmosphere at home.",
        color: "indigo",
      },
    ],
  },
  process: {
    tag: "Family Harmony Process",
    title: "3 Steps to more peace and steadiness at home",
    description: "Private and built around your current family situation.",
    ctaText: "Book Your Consultation Now",
    items: [
      {
        number: "1",
        title: "Book Your Consultation",
        description:
          "Choose your slot and tell us if the concern is tension, emotional distance, or home peace.",
        icon: <IoCalendarOutline />,
      },
      {
        number: "2",
        title: "Share Your Situation",
        description:
          "Share recurring conflict, pressure, misunderstandings, or hard home patterns over a live 1-1 video call.",
        icon: <IoCallOutline />,
      },
      {
        number: "3",
        title: "Get Your Guidance",
        description:
          "Get timing guidance, remedies, and a practical next-step path.",
        icon: <IoCheckmarkCircleOutline />,
      },
    ],
  },
  testimonials: {
    tag: "Family Stories",
    title: "Why people choose Astroji when home stops feeling peaceful",
    description:
      "Clear guidance for home tension, misunderstandings, emotional distance, and recurring stress.",
    stats: sharedTestimonialStats,
    items: [
      {
        name: "Anita J.",
        location: "Chandigarh",
        problem: "Home Tension",
        rating: 5,
        text: "Peace at home never lasted long. The consultation helped me understand the pattern with much more calm.",
      },
      {
        name: "Rohit D.",
        location: "Bhopal",
        problem: "Family Pressure",
        rating: 5,
        text: "The session gave me a clearer view of repeated family pressure. The situation felt less emotionally chaotic.",
      },
      {
        name: "Meera K.",
        location: "Hyderabad",
        problem: "Emotional Distance",
        rating: 5,
        text: "I wanted clarity on why home felt emotionally cold. The guidance was clear and calming.",
      },
    ],
  },
  whyChoose: {
    title: "Why Astroji for Family Guidance?",
    description:
      "We keep family consultations sensitive, private, and practical so advice is easy to use daily.",
    items: [
      {
        icon: <IoShieldCheckmarkOutline />,
        title: "Private Family Conversations",
        desc: "Speak openly about home tension, pressure, emotional distance, or family strain in a secure setting.",
      },
      {
        icon: <IoRibbonOutline />,
        title: "Harmony-Focused Guidance",
        desc: "Our experts decode recurring family patterns and help you respond with steadiness.",
      },
      {
        icon: <IoCheckmarkCircleOutline />,
        title: "Practical Remedies + Direction",
        desc: "The guidance supports peace in real life, not just reassurance.",
      },
      {
        icon: <IoPeopleOutline />,
        title: "Support Beyond the Session",
        desc: "We stay available after the call so your guidance stays supported at home.",
      },
    ],
  },
  bookingCta: {
    liveBadge: "Family Guidance Slots Filling Fast",
    title: "Book Your Family Consultation",
    description:
      "Talk to an Astroji expert about home tension, emotional distance, misunderstandings, or family pressure.",
    buttonText: "Start Your Booking",
    privacyText: "Private Guidance",
    verifiedText: "Verified Experts",
  },
  stickyCta: {
    title: "Need more peace at home?",
    subtitle: "Book your consultation today",
    buttonText: "Book Your Consultation",
  },
};

export const lifeDirectionConsultationLandingConfig: ConsultationLandingConfig = {
  modalTheme: "lifeDirection",
  modalDefaultConcernArea: "Other",
  theme: lifeDirectionTheme,
  hero: {
    badge: "Life Direction Consultation",
    titlePrefix: "Feeling stuck, restless, or directionless?",
    titleHighlight: "Let's understand this phase.",
    descriptionPrefix:
      "Talk to Astroji experts about crossroads, repeated setbacks, inner restlessness, and your next chapter.",
    descriptionHighlight: "A clear view of where to focus next.",
    supportingLine: "When life feels foggy, clear guidance helps.",
    primaryCtaText: "Book Your Consultation",
    secondaryCtaText: "Meet Our Experts",
    stats: [
      {
        icon: <IoPeopleOutline />,
        value: "70,000+",
        label: "Clients Guided",
        iconShellClass: "bg-white/10",
        iconClass: "text-indigo-200 text-lg",
      },
      {
        icon: <IoStar />,
        value: "4.2*",
        label: "Trusted Rating",
        iconShellClass: "bg-white/10",
        iconClass: "text-amber-200 text-lg",
      },
      {
        icon: <IoLockClosedOutline />,
        value: "100%",
        label: "Private Sessions",
        iconShellClass: "bg-white/10",
        iconClass: "text-blue-200 text-lg",
      },
    ],
  },
  experts: {
    tag: "Clarity Guidance Team",
    title: "Life Direction Guidance from Astroji Experts",
    description:
      "Our experts help with confusion, repeated setbacks, restless phases, and major crossroads.",
    emptyState: "Our consultation experts will appear here shortly.",
  },
  reasons: {
    tag: "Clarity Pain Points",
    title: "Common life-direction questions we solve",
    description:
      "If life feels foggy, momentum keeps breaking, or you need change but cannot see the next step, this page is for you.",
    items: [
      {
        icon: <IoSparkles />,
        label: "Feeling Stuck",
        problem: "Effort is there, but progress still feels strangely blocked?",
        solution:
          "Understand whether this phase needs patience, change, healing, or a new direction.",
        color: "indigo",
      },
      {
        icon: <IoBriefcase />,
        label: "Crossroads",
        problem: "Standing between multiple life paths and unsure what to choose?",
        solution:
          "Get chart-based insight on which option aligns with your current cycle.",
        color: "blue",
      },
      {
        icon: <IoCalendarOutline />,
        label: "Timing",
        problem: "Unsure whether this is the right moment to act, pivot, or wait?",
        solution:
          "Get timing guidance so your next major decision feels more aligned.",
        color: "amber",
      },
      {
        icon: <IoHeart />,
        label: "Inner Restlessness",
        problem: "Nothing feels deeply wrong, but nothing feels truly right either?",
        solution:
          "Understand this inner phase and where your energy needs to shift.",
        color: "rose",
      },
      {
        icon: <IoShieldCheckmarkOutline />,
        label: "Repeated Setbacks",
        problem: "You keep trying, but the same kinds of setbacks keep returning?",
        solution:
          "Decode recurring patterns and get remedies for steadier movement.",
        color: "slate",
      },
      {
        icon: <IoPeopleOutline />,
        label: "Next Chapter",
        problem: "You know change is coming, but the next chapter still feels invisible?",
        solution:
          "Leave with perspective on where this phase is leading and how to move forward.",
        color: "cyan",
      },
    ],
  },
  benefits: {
    tag: "Clarity Consultation Benefits",
    title: "What this life-direction consultation gives you",
    description:
      "This session helps you understand your phase, release confusion, and move with intention.",
    items: [
      {
        icon: <IoSparkles />,
        label: "Clarity",
        problem: "A Clearer View of This Phase",
        solution:
          "Understand what this life chapter is asking of you and why things feel blocked.",
        color: "indigo",
      },
      {
        icon: <IoCalendarOutline />,
        label: "Timing",
        problem: "Better Timing for Change",
        solution:
          "See better windows for major decisions, fresh starts, and redirection.",
        color: "amber",
      },
      {
        icon: <IoShieldCheckmarkOutline />,
        label: "Remedies",
        problem: "Remedies for Repeated Blocks",
        solution:
          "Get remedies that support confidence and forward movement.",
        color: "blue",
      },
      {
        icon: <IoCheckmarkCircleOutline />,
        label: "Direction",
        problem: "A More Grounded Next Step",
        solution:
          "Leave with practical direction so the future feels easier to approach.",
        color: "green",
      },
    ],
  },
  process: {
    tag: "Clarity Process",
    title: "3 Steps to a clearer next chapter",
    description: "Simple, private, and built around your current uncertainty.",
    ctaText: "Book Your Consultation Now",
    items: [
      {
        number: "1",
        title: "Book Your Consultation",
        description:
          "Choose your slot and tell us where life feels blocked, unclear, or directionless.",
        icon: <IoCalendarOutline />,
      },
      {
        number: "2",
        title: "Share Your Situation",
        description:
          "Share the crossroads, setbacks, inner restlessness, or repeated pattern affecting you over a live 1-1 video call.",
        icon: <IoCallOutline />,
      },
      {
        number: "3",
        title: "Get Your Guidance",
        description:
          "Get timing guidance, remedies, and a grounded direction for your next chapter.",
        icon: <IoCheckmarkCircleOutline />,
      },
    ],
  },
  testimonials: {
    tag: "Clarity Stories",
    title: "Why people choose Astroji when life feels foggy",
    description:
      "Clear guidance for confusion, crossroads, repeated setbacks, and uncertain phases.",
    stats: sharedTestimonialStats,
    items: [
      {
        name: "Dev M.",
        location: "Bengaluru",
        problem: "Crossroads",
        rating: 5,
        text: "I was stuck between multiple life choices and trusted none of them. The consultation brought real clarity.",
      },
      {
        name: "Ayesha F.",
        location: "Kolkata",
        problem: "Feeling Stuck",
        rating: 5,
        text: "I knew I was in a strange phase but could not name it. The session helped me understand the pattern and stop panicking.",
      },
      {
        name: "Pranav L.",
        location: "Mumbai",
        problem: "Repeated Setbacks",
        rating: 5,
        text: "I was tired of the same blocks returning in different forms. The guidance felt grounding and useful.",
      },
    ],
  },
  whyChoose: {
    title: "Why Astroji for Life Direction Guidance?",
    description:
      "We combine timing, pattern insight, and practical remedies so the guidance feels like real direction.",
    items: [
      {
        icon: <IoShieldCheckmarkOutline />,
        title: "Private Clarity Conversations",
        desc: "Speak openly about confusion, fear, restlessness, or uncertainty in a secure setting.",
      },
      {
        icon: <IoRibbonOutline />,
        title: "Thoughtful Direction Support",
        desc: "Our experts help clients understand crossroads, transitions, and repeated blocks.",
      },
      {
        icon: <IoCheckmarkCircleOutline />,
        title: "Practical Remedies + Next Steps",
        desc: "The advice helps you move with intention, not just feel inspired for a moment.",
      },
      {
        icon: <IoPeopleOutline />,
        title: "Support Beyond the Session",
        desc: "We stay available after the call so your guidance stays anchored.",
      },
    ],
  },
  bookingCta: {
    liveBadge: "Clarity Slots Filling Fast",
    title: "Book Your Life Direction Consultation",
    description:
      "Talk to an Astroji expert about confusion, crossroads, repeated setbacks, or your next life chapter.",
    buttonText: "Start Your Booking",
    privacyText: "Private Guidance",
    verifiedText: "Verified Experts",
  },
  stickyCta: {
    title: "Need a clearer sense of direction?",
    subtitle: "Book your consultation today",
    buttonText: "Book Your Consultation",
  },
};

export const consultationIssueLandingConfigs = {
  "career-job": careerJobConsultationLandingConfig,
  "love-marriage": loveMarriageConsultationLandingConfig,
  finance: financeConsultationLandingConfig,
  children: childrenConsultationLandingConfig,
  family: familyConsultationLandingConfig,
  "life-direction": lifeDirectionConsultationLandingConfig,
} as const;

export type ConsultationIssueSlug = keyof typeof consultationIssueLandingConfigs;
